import React, { useState, useEffect, useMemo, useCallback } from 'react'
import api from '../services/api'
import { useIsMobile } from '../hooks/useIsMobile'
import toast from 'react-hot-toast'
import {
  Gavel,
  Search,
  RefreshCw,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  TrendingUp,
  DollarSign,
  Zap,
  Activity,
  Users,
  ChevronLeft,
  ChevronRight,
  Eye,
  ArrowRight,
  Filter,
  Send,
  Pause,
  Play
} from 'lucide-react'
import { format, parseISO } from 'date-fns'

function fmtDate(val) {
  if (!val) return '—'
  try { return format(parseISO(val), 'dd/MM/yy HH:mm:ss') } catch { return val }
}

function getDateRangeSinceIso(dateRange) {
  const now = new Date()
  if (dateRange === 'today') {
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString()
  }
  if (dateRange === '7d') {
    return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString()
  }
  if (dateRange === '30d') {
    return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString()
  }
  if (dateRange === 'ytd') {
    return new Date(now.getFullYear(), 0, 1).toISOString()
  }
  return null
}

function DateRangeSelector({ value, onChange }) {
  const options = [
    { id: 'today', label: 'Today' },
    { id: '7d',    label: '7 Days' },
    { id: '30d',   label: '30 Days' },
    { id: 'ytd',   label: 'YTD' },
  ]
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', background: 'var(--bg-raised)',
      padding: 3, borderRadius: 10, border: '1px solid var(--border)', gap: 2
    }}>
      {options.map(opt => (
        <button
          key={opt.id} type="button" onClick={() => onChange(opt.id)}
          style={{
            padding: '5px 12px', fontSize: 12, fontWeight: value === opt.id ? 700 : 500,
            borderRadius: 7, border: 'none', cursor: 'pointer',
            background: value === opt.id ? 'var(--accent)' : 'transparent',
            color: value === opt.id ? '#ffffff' : 'var(--text-secondary)',
            transition: 'all 0.15s ease',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

const POLL_INTERVAL_MS = 5000

// ── Auction Status Badge Component ──────────────────────────────────────────────
function AuctionStatusBadge({ status, amount }) {
  if (status === 'won' || status === 'sold') {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px',
        background: 'var(--green-bg, #DCFCE7)', color: 'var(--green, #166534)',
        border: '1px solid #BBF7D0', borderRadius: 999, fontWeight: 700, fontSize: 11,
      }}>
        <CheckCircle2 size={12} /> WON (${amount ? Number(amount).toFixed(2) : '0.00'})
      </span>
    )
  }
  if (status === 'no_bids' || status === 'below_floor') {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px',
        background: 'var(--amber-bg, #FEF3C7)', color: 'var(--amber, #92400E)',
        border: '1px solid #FDE68A', borderRadius: 999, fontWeight: 700, fontSize: 11,
      }}>
        <AlertCircle size={12} /> NO WINNER
      </span>
    )
  }
  if (status === 'rejected' || status === 'failed') {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px',
        background: 'var(--red-bg, #FEE2E2)', color: 'var(--red, #991B1B)',
        border: '1px solid #FECACA', borderRadius: 999, fontWeight: 700, fontSize: 11,
      }}>
        <XCircle size={12} /> REJECTED
      </span>
    )
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px',
      background: 'var(--blue-bg, #E0F2FE)', color: 'var(--blue, #0369A1)',
      border: '1px solid #BAE6FD', borderRadius: 999, fontWeight: 700, fontSize: 11,
    }}>
      <Clock size={12} /> AUCTION ACTIVE
    </span>
  )
}

// ── Auction Detail Drawer / Modal ─────────────────────────────────────────────
function AuctionDetailModal({ leadId, onClose }) {
  const [leadData, setLeadData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api.get(`/leads/${encodeURIComponent(leadId)}/timeline`)
      .then(({ data }) => { if (!cancelled) setLeadData(data) })
      .catch(() => toast.error(`Could not load auction timeline for lead ${leadId}`))
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [leadId])

  const auctionEvents = useMemo(() => {
    if (!leadData?.events) return []
    return leadData.events.filter(e => 
      e.event_type.includes('buyer') || 
      e.event_type.includes('bid') || 
      e.event_type.includes('postback') ||
      e.event_type.includes('flow')
    )
  }, [leadData])

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 10000 }}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900, width: '92%' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Gavel size={20} color="var(--accent)" /> Ping/Post Auction Breakdown — Lead #{leadId}
          </h3>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
        </div>
        
        <div className="modal-body" style={{ flexDirection: 'column', gap: 18, maxHeight: '75vh', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div>
          ) : !leadData ? (
            <p style={{ color: 'var(--text-muted)' }}>Auction lead details not available.</p>
          ) : (
            <>
              {/* Summary Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <div style={{ background: 'var(--bg-base)', padding: 12, borderRadius: 10, border: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Vertical</span>
                  <div style={{ fontSize: 16, fontWeight: 700, textTransform: 'capitalize' }}>{leadData.vertical || '—'}</div>
                </div>
                <div style={{ background: 'var(--bg-base)', padding: 12, borderRadius: 10, border: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Winning Revenue</span>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--green)' }}>
                    {leadData.revenue ? `$${Number(leadData.revenue).toFixed(2)}` : '$0.00'}
                  </div>
                </div>
                <div style={{ background: 'var(--bg-base)', padding: 12, borderRadius: 10, border: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Status</span>
                  <div><AuctionStatusBadge status={leadData.status} amount={leadData.revenue} /></div>
                </div>
              </div>

              {/* Bidding Sequence */}
              <div>
                <h4 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
                  Auction Bidding Sequence ({auctionEvents.length} events)
                </h4>
                {auctionEvents.length === 0 ? (
                  <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No bid responses recorded for this auction.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {auctionEvents.map((evt, idx) => {
                      const payload = evt.payload || {}
                      const isBid = evt.event_type === 'buyer_bid_received'
                      const isWinner = isBid && payload.amount && (payload.amount === leadData.revenue || payload.result === 'BID')
                      
                      return (
                        <div key={evt.id || idx} style={{
                          padding: 14, borderRadius: 10, background: 'var(--bg-base)',
                          border: isWinner ? '1px solid var(--green)' : '1px solid var(--border)',
                          boxShadow: isWinner ? '0 0 0 1px var(--green)' : 'none'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontWeight: 700, fontSize: 13, color: isWinner ? 'var(--green)' : 'var(--text-primary)' }}>
                                {evt.event_type_label}
                              </span>
                              {payload.buyer_name && (
                                <span className="badge badge-blue">{payload.buyer_name}</span>
                              )}
                            </div>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                              {fmtDate(evt.created_at)}
                            </span>
                          </div>

                          <div style={{ display: 'flex', gap: 16, fontSize: 12, flexWrap: 'wrap', color: 'var(--text-secondary)' }}>
                            {payload.amount != null && (
                              <div><strong>Bid Amount:</strong> <span style={{ color: 'var(--green)', fontWeight: 700 }}>${Number(payload.amount).toFixed(2)}</span></div>
                            )}
                            {payload.latency_ms != null && (
                              <div><strong>Latency:</strong> {payload.latency_ms} ms</div>
                            )}
                            {payload.reason && (
                              <div><strong>Reason:</strong> <span style={{ color: 'var(--amber)' }}>{payload.reason}</span></div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

// ── Main Auction Validator Component ─────────────────────────────────────────
export default function AuctionValidator() {
  const isMobile = useIsMobile()
  const [isAutoPolling, setIsAutoPolling] = useState(true)
  const [loading, setLoading] = useState(false)
  const [leads, setLeads] = useState([])
  const [searchId, setSearchId] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [dateRange, setDateRange] = useState('today')
  const [selectedLeadId, setSelectedLeadId] = useState(null)
  const [page, setPage] = useState(1)

  const fetchAuctionLeads = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/auction/leads', {
        params: {
          limit: 50,
          date_range: dateRange,
          status: statusFilter,
          search: searchId
        }
      }).catch(() => null)
      
      if (res?.data?.leads && Array.isArray(res.data.leads)) {
        const mapped = res.data.leads.map(l => ({
          id: l.kickoff_lead_id || l.id,
          created_at: l.created_at || new Date().toISOString(),
          vertical: l.vertical || 'solar',
          status: l.status === 'sold' || l.sold || l.payout > 0 ? 'sold' : l.status === 'rejected' ? 'rejected' : 'no_winner',
          revenue: Number(l.revenue ?? l.payout ?? l.price ?? 0),
          winning_buyer: l.winner_name || l.winning_buyer || l.lender || '—',
          bids_received: l.bids_count ?? l.bids_received ?? (l.status === 'sold' ? 1 : 0),
          highest_bid: Number(l.highest_bid ?? l.revenue ?? l.payout ?? 0),
          avg_latency: l.latency_ms ?? l.avg_latency ?? 140
        }))
        setLeads(mapped)
      } else {
        setLeads([])
      }
    } catch {
      setLeads([])
    } finally {
      setLoading(false)
    }
  }, [dateRange, statusFilter, searchId])

  useEffect(() => {
    fetchAuctionLeads()
  }, [fetchAuctionLeads])

  // Auto-polling interval toggle
  useEffect(() => {
    if (!isAutoPolling) return
    const interval = setInterval(() => {
      fetchAuctionLeads()
    }, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [isAutoPolling, fetchAuctionLeads])

  const filteredLeads = useMemo(() => {
    const sinceIso = getDateRangeSinceIso(dateRange)
    const sinceTime = sinceIso ? new Date(sinceIso).getTime() : 0

    return leads.filter(l => {
      if (sinceTime && l.created_at) {
        const leadTime = new Date(l.created_at).getTime()
        if (leadTime < sinceTime) return false
      }
      if (searchId && !String(l.id).includes(searchId.trim())) return false
      if (statusFilter !== 'all') {
        if (statusFilter === 'sold' && l.status !== 'sold') return false
        if (statusFilter === 'no_winner' && l.status !== 'no_winner') return false
        if (statusFilter === 'rejected' && l.status !== 'rejected') return false
      }
      return true
    })
  }, [leads, searchId, statusFilter, dateRange])

  // Calculate live auction metrics
  const metrics = useMemo(() => {
    const total = leads.length || 1
    const won = leads.filter(l => l.status === 'sold').length
    const totalRevenue = leads.reduce((acc, l) => acc + (Number(l.revenue) || 0), 0)
    const avgLatency = Math.round(leads.reduce((acc, l) => acc + (l.avg_latency || 0), 0) / total)

    return {
      totalAuctionPings: total,
      winRate: Math.round((won / total) * 100),
      totalRevenue: totalRevenue,
      avgBidPrice: won > 0 ? (totalRevenue / won).toFixed(2) : '0.00',
      avgLatencyMs: avgLatency || 140
    }
  }, [leads])

  return (
    <div className="fade-in page-shell">
      {/* Hero Header */}
      <section className="page-hero compact-hero">
        <div className="page-hero-content">
          <span className="hero-kicker">Real-Time Bidding (RTB) Validator</span>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Gavel size={28} color="var(--accent)" /> Ping/Post Auction Validator
          </h1>
          <p>Live ping/post auction monitoring engine. Validates real-time buyer bid responses, minimum floor prices, and execution latencies.</p>
          <div className="hero-chips">
            <span className="badge badge-blue">PING/POST Engine Active</span>
            <span className="badge badge-green">Live Bidding Streams</span>
          </div>
        </div>

        {/* Polling Control Button */}
        <button
          className={`btn ${isAutoPolling ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setIsAutoPolling(!isAutoPolling)}
          style={{ height: 42, padding: '0 18px', gap: 8, flexShrink: 0 }}
        >
          {isAutoPolling ? <Pause size={16} /> : <Play size={16} />}
          {isAutoPolling ? 'Auto-Refresh ON' : 'Paused'}
        </button>
      </section>

      {/* KPI Cards Grid */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(170px, 1fr))',
        gap: 14, marginBottom: 16
      }}>
        <div className="card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 10, background: 'var(--blue-bg)', color: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Activity size={20} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Auction Pings</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{metrics.totalAuctionPings}</div>
          </div>
        </div>

        <div className="card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 10, background: 'var(--green-bg)', color: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TrendingUp size={20} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Win Rate</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{metrics.winRate}%</div>
          </div>
        </div>

        <div className="card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 10, background: 'var(--purple-bg)', color: 'var(--purple)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DollarSign size={20} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Avg Winning Bid</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>${metrics.avgBidPrice}</div>
          </div>
        </div>

        <div className="card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 10, background: 'var(--amber-bg)', color: 'var(--amber)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Zap size={20} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Avg Buyer Latency</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{metrics.avgLatencyMs} ms</div>
          </div>
        </div>
      </section>

      {/* Filter Toolbar */}
      <section className="card section-card toolbar-card" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <Search size={14} className="search-icon" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            className="input"
            placeholder="Search by Lead ID..."
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
            style={{ paddingLeft: 38, height: 42 }}
          />
        </div>

        <DateRangeSelector value={dateRange} onChange={setDateRange} />

        <select
          className="input"
          style={{ width: 170, height: 42 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Auction Statuses</option>
          <option value="sold">✅ Auction Won</option>
          <option value="no_winner">⚠️ No Winner</option>
          <option value="rejected">❌ Rejected</option>
        </select>

        <button
          className="btn btn-ghost"
          onClick={fetchAuctionLeads}
          disabled={loading}
          style={{ height: 42, padding: '0 18px' }}
        >
          <RefreshCw size={14} className={loading ? 'spin' : ''} />
          Refresh Feed
        </button>
      </section>

      {/* Auction Feed Table */}
      <section className="card section-card table-card-shell">
        <div className="table-wrap table-wrap-elevated">
          <table>
            <thead>
              <tr>
                <th style={{ width: 140 }}>Timestamp</th>
                <th>Lead ID</th>
                <th>Vertical</th>
                <th>Bids Received</th>
                <th>Highest Bid</th>
                <th>Winning Buyer</th>
                <th>Status</th>
                <th style={{ width: 80 }}>Inspect</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
                    No auction pings matching current criteria.
                  </td>
                </tr>
              ) : (
                filteredLeads.map((item) => (
                  <tr key={item.id}>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{fmtDate(item.created_at)}</td>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>#{item.id}</td>
                    <td style={{ textTransform: 'capitalize' }}>{item.vertical}</td>
                    <td>
                      <span className="badge badge-blue" style={{ fontWeight: 700 }}>
                        {item.bids_received} buyer bids
                      </span>
                    </td>
                    <td style={{ fontWeight: 700, color: item.highest_bid > 0 ? 'var(--green)' : 'var(--text-muted)' }}>
                      ${Number(item.highest_bid || 0).toFixed(2)}
                    </td>
                    <td style={{ fontWeight: 600 }}>{item.winning_buyer || '—'}</td>
                    <td><AuctionStatusBadge status={item.status} amount={item.revenue} /></td>
                    <td>
                      <button
                        className="btn btn-ghost btn-sm btn-icon"
                        title="Inspect Auction Timeline"
                        onClick={() => setSelectedLeadId(item.id)}
                      >
                        <Eye size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Inspector Modal */}
      {selectedLeadId && (
        <AuctionDetailModal
          leadId={selectedLeadId}
          onClose={() => setSelectedLeadId(null)}
        />
      )}
    </div>
  )
}
